// Pure validation for the Event Idea authoring form.
//
// Limits mirror the backend `StoreEventIdeaRequest` exactly — keep them in sync
// with arvee-backend, not with whatever a design mock happens to show. Follows
// Vuetify's `:rules` convention: each rule returns `true` or an error string.

import type { EventIdeaLocationHint } from '@@/types/api';

type ValidationRule = (value: unknown) => true | string;

// ---------------------------------------------------------------------------
// Backend field limits (`StoreEventIdeaRequest`)
// ---------------------------------------------------------------------------

export const IDEA_TITLE_MAX = 120;
export const IDEA_DESCRIPTION_MAX = 5000;
export const IDEA_LOCATION_DISPLAY_MAX = 255;
export const IDEA_REPORT_REASON_MAX = 1000;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isNonEmptyString(value: unknown): boolean {
  return typeof value === 'string' && value.trim() !== '';
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

// ---------------------------------------------------------------------------
// Field rules (Vuetify :rules arrays)
// ---------------------------------------------------------------------------

export const ideaTitleRules: ValidationRule[] = [
  (v) => isNonEmptyString(v) || 'A title is required',
  (v) =>
    typeof v !== 'string' ||
    v.length <= IDEA_TITLE_MAX ||
    `Title must be ${IDEA_TITLE_MAX} characters or fewer`,
];

export const ideaDescriptionRules: ValidationRule[] = [
  (v) => isNonEmptyString(v) || 'A description is required',
  (v) =>
    typeof v !== 'string' ||
    v.length <= IDEA_DESCRIPTION_MAX ||
    `Description must be ${IDEA_DESCRIPTION_MAX} characters or fewer`,
];

export const ideaLocationRules: ValidationRule[] = [
  (v) => isNonEmptyString(v) || 'A location is required',
  (v) =>
    typeof v !== 'string' ||
    v.length <= IDEA_LOCATION_DISPLAY_MAX ||
    `Location must be ${IDEA_LOCATION_DISPLAY_MAX} characters or fewer`,
];

export const ideaReportReasonRules: ValidationRule[] = [
  (v) => isNonEmptyString(v) || 'Tell us what is wrong with this idea',
  (v) =>
    typeof v !== 'string' ||
    v.length <= IDEA_REPORT_REASON_MAX ||
    `Reason must be ${IDEA_REPORT_REASON_MAX} characters or fewer`,
];

// ---------------------------------------------------------------------------
// Location hint — coords are NON-NEGOTIABLE [S1]
// ---------------------------------------------------------------------------

/**
 * The form's in-progress location state. Coordinates are `null` until an
 * autocomplete suggestion is actually selected — typing free text alone leaves
 * them null, which is exactly the case this validator exists to reject.
 */
export interface IdeaLocationInput {
  display?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

/**
 * Validate the `location_hint` sub-object. Returns a map keyed by the BACKEND
 * field paths so the result merges straight into the same `serverErrors` bag a
 * 422 populates. An empty object means valid.
 *
 * Free-text with no resolved coordinates is rejected: the feed's bounding-box
 * filter reads the denormalized lat/lng, so an uncoordinated idea would be
 * invisible on every map-driven surface.
 */
export function validateIdeaLocation(
  hint: IdeaLocationInput | EventIdeaLocationHint | null | undefined,
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!hint) {
    errors['location_hint.display'] = 'A location is required.';
    errors['location_hint.latitude'] =
      'Pick a location from the suggestions so we can place it on the map.';
    return errors;
  }

  if (!isNonEmptyString(hint.display)) {
    errors['location_hint.display'] = 'A location is required.';
  } else if ((hint.display as string).length > IDEA_LOCATION_DISPLAY_MAX) {
    errors['location_hint.display'] =
      `Location must be ${IDEA_LOCATION_DISPLAY_MAX} characters or fewer.`;
  }

  if (!isFiniteNumber(hint.latitude) || !isFiniteNumber(hint.longitude)) {
    errors['location_hint.latitude'] =
      'Pick a location from the suggestions so we can place it on the map.';
    return errors;
  }

  if (hint.latitude < -90 || hint.latitude > 90) {
    errors['location_hint.latitude'] =
      'That latitude is outside the valid range.';
  }
  if (hint.longitude < -180 || hint.longitude > 180) {
    errors['location_hint.longitude'] =
      'That longitude is outside the valid range.';
  }

  return errors;
}
